// PercentStackedBarChart.js
import React from 'react';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale } from 'chart.js';
import ChartjsPluginStacked100 from 'chartjs-plugin-stacked100';

// Register necessary components for Chart.js
ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale, ChartjsPluginStacked100);

const StackedPercentageBarChart = ({ chartData, colors }) => {
  
  // Create and style custom tooltip
  const getOrCreateTooltip = (chart) => {
    let tooltipEl = chart.canvas.parentNode.querySelector('div');

    if (!tooltipEl) {
      tooltipEl = document.createElement('div');
      tooltipEl.classList.add('customChartJsToolTop');
      tooltipEl.style.position = 'absolute';
      tooltipEl.style.pointerEvents = 'none';
      tooltipEl.style.backgroundColor = 'white';
      tooltipEl.style.border = '1px solid #ccc';
      tooltipEl.style.borderRadius = '3px';
      tooltipEl.style.padding = '5px';
      tooltipEl.style.boxShadow = '0px 0px 6px rgba(0, 0, 0, 0.2)';

      const table = document.createElement('table');
      table.style.margin = '0px';
      tooltipEl.appendChild(table);
      chart.canvas.parentNode.appendChild(tooltipEl);
    }

    return tooltipEl;
  };

  const externalTooltipHandler = (context) => {
    const { chart, tooltip } = context;
    const tooltipEl = getOrCreateTooltip(chart);

    // Hide if no tooltip
    if (tooltip.opacity === 0) {
      tooltipEl.style.opacity = 0;
      return;
    }

    // Set Text
    if (tooltip.body) {
      const titleLines = tooltip.title || [];
      const bodyLines = tooltip.body.map(b => b.lines);

      const tableHead = document.createElement('thead');
      titleLines.forEach(title => {
        const tr = document.createElement('tr');
        const th = document.createElement('th');
        th.textContent = title;
        th.style.borderWidth = 0;
        tr.appendChild(th);
        tableHead.appendChild(tr);
      });

      const tableBody = document.createElement('tbody');
      bodyLines.forEach((body, i) => {
        const colors = tooltip.labelColors[i];
        const span = document.createElement('span');
        span.style.background = colors.backgroundColor;
        span.style.borderColor = colors.borderColor;
        span.style.borderWidth = '2px';
        span.style.marginRight = '10px';
        span.style.height = '10px';
        span.style.width = '10px';
        span.style.display = 'inline-block';

        const tr = document.createElement('tr');
        const td = document.createElement('td');
        td.style.borderWidth = 0;
        td.appendChild(span);
        td.appendChild(document.createTextNode(body));
        tr.appendChild(td);
        tableBody.appendChild(tr);
      });

      const tableRoot = tooltipEl.querySelector('table');
      while (tableRoot.firstChild) {
        tableRoot.firstChild.remove();
      }
      tableRoot.appendChild(tableHead);
      tableRoot.appendChild(tableBody);
    }

    const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas;
    tooltipEl.style.opacity = 1;
    tooltipEl.style.left = `${positionX + tooltip.caretX}px`;
    tooltipEl.style.top = `${positionY + tooltip.caretY }px`;
    tooltipEl.style.font = tooltip.options.bodyFont.string;
    tooltipEl.style.padding = `${tooltip.options.padding}px ${tooltip.options.padding}px`;
    tooltipEl.style.width = 'auto';
    tooltipEl.style.zIndex = '9';
    tooltipEl.style.color = '#000';
  };

  // Use the colors array to set dataset colors
  const datasets = chartData.datasets.map((dataset, index) => ({
    ...dataset,
    backgroundColor: colors[index] || '#000000' // Default to black if color is not provided
  }));

  // Data configuration
  const data = {
    ...chartData,
    datasets
  };

  // Options configuration
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'x',
    plugins: {
      stacked100: { enable: true },
      title: { display: false },
      datalabels: { display: false },
      legend: { 
        display: true, // Show or hide the legend
          position: 'bottom', // Position of the legend
          align: 'center', // Alignment of the legend
          labels: {             
            padding: (0, 15, 0, 10), // Padding around the legend labels
            boxWidth: 7, // Width of the color box in the legend
            boxHeight: 7, // Height of the color box in the legend
            usePointStyle: true, // Use point styles instead of boxes             
          },
          title: {
              display: true,
              padding: -7,
          },
      },
      tooltip: {
        enabled: true,
        position: 'average',
        callbacks: {
          label: function(tooltipItem) {
            return tooltipItem.dataset.label + ': ' + tooltipItem.raw;
          },
        },
        external: externalTooltipHandler,
      },
    },
    layout: {
      padding: {
        bottom: 0,
      },
    },
    scales: {
      x: {
        display: true,
        stacked: true,
        beginAtZero: true,
        grid: { display: false },
        title: { display: false },
        ticks: { display: true },
      },
      y: {
        display: true,
        stacked: true,
        grid: { display: false },
        ticks: { display: true },
      },
    },
   // barThickness: 12,
  };

  return <Bar data={data} options={options} />;
};

export default StackedPercentageBarChart;
